import { RainbowKitProvider } from "@rainbow-me/rainbowkit"
import { WalletKitProvider } from "react-walletkit"
import { Chain } from "viem"
import {
  Context,
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState
} from "react"
import { BytesLike } from "ethers"
import { useAccount, useBalance, useNetwork, useSwitchNetwork } from "wagmi"
import { ProductCart } from "@src/lib/cart/cartProductTypes"
import Modal from "../Modal"
import { View } from "../Modal/modals/types"
import SliceLogo from "../icons/SliceLogo"
import { Toaster } from "../ui/base/toaster"
import { constants } from "@/src"

export type Purchase = {
  slicerId: string
  productId: string
  totalQuantity: string
  buyerCustomData: BytesLike
}

type Theme = {
  cartButtonColor?: string
  textColor?: string
  backgroundColor?: string
  accentColor?: string
  accentBackgroundColor?: string
}

type SliceContextType = {
  appChainId: number
  modalView: View
  setModalView: Dispatch<SetStateAction<View>>
  isSwitchingNetwork: boolean
  isModalTrigger: number
  purchases: Purchase[]
  cart: ProductCart[]
  setPurchases: Dispatch<SetStateAction<Purchase[]>>
  updateCart: (newCart: ProductCart[]) => void
  switchNetwork: (chainId_?: number) => void
  setIsModalTrigger: Dispatch<SetStateAction<number>>
  checkoutCompleted: boolean
  setCheckoutCompleted: Dispatch<SetStateAction<boolean>>
  isUpdatingCart: boolean
  setIsUpdatingCart: Dispatch<SetStateAction<boolean>>
  isConnected: boolean
  setIsConnected: Dispatch<SetStateAction<boolean>>
  notificationReceiver: string | undefined
  isReady: boolean
  setIsReady: Dispatch<SetStateAction<boolean>>
  hash: string
  setHash: Dispatch<SetStateAction<string>>
  theme: Theme
  accountBalance: {
    decimals: number
    formatted: string
    symbol: string
    value: bigint
  }
  refetchBalance: (options?: {
    throwOnError: boolean
    cancelRefetch: boolean
  }) => Promise<any>
}

const SliceContext: Context<SliceContextType> = createContext({
  appChainId: null,
  modalView: { name: "" },
  setModalView: null,
  isSwitchingNetwork: false,
  isModalTrigger: 0,
  purchases: [],
  cart: [],
  setPurchases: null,
  updateCart: null,
  switchNetwork: null,
  setIsModalTrigger: null,
  checkoutCompleted: false,
  setCheckoutCompleted: null,
  isUpdatingCart: false,
  setIsUpdatingCart: null,
  isConnected: false,
  setIsConnected: null,
  notificationReceiver: null,
  isReady: false,
  setIsReady: null,
  hash: "",
  setHash: null,
  theme: null,
  accountBalance: null,
  refetchBalance: null
})

export function useSliceContext() {
  return useContext(SliceContext)
}

const SliceProvider = ({
  chains,
  children,
  initCart,
  notificationReceiver,
  theme = {}
}: {
  chains: Chain[]
  children: React.ReactNode
  initCart?: ProductCart[]
  notificationReceiver?: string
  theme?: Theme
}) => {
  const { address: account } = useAccount()
  const { chain } = useNetwork()
  const appChainId = chains?.[0].id
  const [isModalTrigger, setIsModalTrigger] = useState(0)
  const [modalView, setModalView] = useState<View>({
    name: ""
  })
  const { switchNetwork, isLoading: isSwitchingNetwork } = useSwitchNetwork({
    chainId: appChainId
  })
  const [isConnected, setIsConnected] = useState(false)
  const [cart, setCart] = useState<ProductCart[]>(
    initCart ||
      (typeof window !== "undefined" &&
        JSON.parse(localStorage.getItem("cart"))) ||
      []
  )
  const [checkoutCompleted, setCheckoutCompleted] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [hash, setHash] = useState("")

  const updateCart = (newCart: ProductCart[]) => {
    setCart(newCart)
    localStorage.setItem("cart", JSON.stringify(newCart))
  }

  const [purchases, setPurchases] = useState<Purchase[]>(null)
  // const { address } = useAccount()
  // useEffect(() => {
  //   setPurchases(null)
  //   if (address) {
  //     getPurchases(address, setPurchases)
  //   }
  // }, [address])
  const [isActive, setIsActive] = useState(true)
  const [isUpdatingCart, setIsUpdatingCart] = useState(false)

  const { data: accountBalance, refetch: refetchBalance } = useBalance({
    address: account,
    token: constants[appChainId].addresses["USDCPriceFeed"],
    formatUnits: 6,
    scopeKey: "slice"
  })

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      setIsActive(true)
    } else {
      setIsActive(false)
    }
  }

  useEffect(() => {
    if (modalView?.name != "TOPUP_VIEW") {
      const switchChain = () => {
        try {
          switchNetwork?.(appChainId)
        } catch (error) {
          console.log(error)
        }
        if (Number(chain?.id) !== appChainId) {
          setModalView({
            name: ""
          })
          setTimeout(() => {
            setModalView({
              cross: false,
              name: "NETWORK_VIEW",
              params: { userChainId: chain.id }
            })
          }, 0)
        }
      }

      // Attach the event listener for visibility change
      document.addEventListener("visibilitychange", handleVisibilityChange)

      if (account && chain && Number(chain.id) !== appChainId && isActive) {
        switchChain()
      } else {
        if (modalView.name == "NETWORK_VIEW") {
          setModalView({ name: "" })
        }
      }
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange)
      }
    }
  }, [account, chain, switchNetwork, isActive, isModalTrigger])

  useEffect(() => {
    setIsConnected(!!account)
  }, [account])

  return (
    <RainbowKitProvider chains={chains} showRecentTransactions={true}>
      <WalletKitProvider
        appLogo={<SliceLogo style={{ width: "2rem", height: "2rem" }} />}
      >
        <SliceContext.Provider
          value={{
            appChainId,
            modalView,
            setModalView,
            purchases,
            setPurchases,
            cart,
            updateCart,
            switchNetwork,
            isSwitchingNetwork,
            isModalTrigger,
            setIsModalTrigger,
            checkoutCompleted,
            setCheckoutCompleted,
            isUpdatingCart,
            setIsUpdatingCart,
            isConnected,
            setIsConnected,
            notificationReceiver,
            isReady,
            setIsReady,
            hash,
            setHash,
            theme,
            accountBalance,
            refetchBalance
          }}
        >
          <div>
            {children}
            {modalView.name && <Modal modalView={modalView} />}
            <Toaster />
          </div>
        </SliceContext.Provider>
      </WalletKitProvider>
    </RainbowKitProvider>
  )
}

export default SliceProvider
